import React, { useEffect, useState } from "react";
import * as api from "../../common/athleteCommon/API";
import * as func from "../../common/athleteCommon/FunctionCalls";
import Utils from "../../common/athleteCommon/Utils";
import ReactApexChart from "react-apexcharts";
import { Button } from "react-bootstrap";
import Select from "react-select";
import type { ApexOptions } from "apexcharts";
import ImageWithBasePath from "../../../core/data/img/ImageWithBasePath";

interface AnalyticsResponse {
    athletes?: any[];
    comparison?: Record<string, any>;
}

const ExternalCompAnalytics = () => {

    const [athleteList, setAthleteList] = useState<any[]>([])
    const [sinclairList, setSinclairList] = useState<any[]>([])
    const [analyticList, setAnalyticList] = useState<AnalyticsResponse | null>(null)
    const [athleteListByWeight, setAthleteListByWeight] = useState<any[]>([])
    const [athlete1Id, setAthlete1Id] = useState("");
    const [athlete2Id, setAthlete2Id] = useState("");
    const [athlete1Name, setAthlete1Name] = useState("");
    const [athlete2Name, setAthlete2Name] = useState("");
    const [cycleId, setCycleId] = useState("");
    const [genderId, setGenderId] = useState("");
    const [isLoading, setIsLoading] = useState(false)

    const genderList = [{ extComGenderId: 1, extComGenderName: "Men" }, { extComGenderId: 2, extComGenderName: "Women" }];

    useEffect(() => {
        async function fetchCompetitionAthletes() {
            setIsLoading(true);
            // const athleteResponse = await func.externalCompAthletes(null);
            // setAthleteList(Array.isArray(athleteResponse) ? athleteResponse : []);
            const athleteResponse = await func.externalCompAthletes(null);

            if (Array.isArray(athleteResponse)) {
                setAthleteList(athleteResponse);

                // Select first athlete by default
                if (athleteResponse.length > 0) {
                    const firstAthlete = athleteResponse[0];

                    setAthlete1Id(firstAthlete.extComAthleteId);
                    setAthlete1Name(firstAthlete.extComAthleteName);

                    // Get weight details for the first athlete
                    const athltResponse = await func.getAthletesByWeight(
                        firstAthlete.extComAthleteId
                    );

                    setAthleteListByWeight(
                        Array.isArray(athltResponse)
                            ? athltResponse
                            : []
                    );
                }
            } else {
                setAthleteList([]);
            }
            const sinclairConstantsResponse = await func.externalCompSinclairConts(null);
            setSinclairList(Array.isArray(sinclairConstantsResponse) ? sinclairConstantsResponse : [])
            setIsLoading(false);
        }
        fetchCompetitionAthletes();
    }, [])

    function getAnalytics(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        const formData = new FormData();
        formData.append("athleteId1", athlete1Id);
        formData.append("athleteId2", athlete2Id);
        formData.append("cycle", cycleId);
        formData.append("gender", genderId);
        const analytics = api.ExternalCompetitionAnalytics;
        fetch(analytics, {
            method: "POST",
            headers: {
                "Authorization": 'Bearer ' + Utils.getAccessToken()
            },
            body: formData
        })
            .then((response) => response.json())
            .then((result) => {
                if (result.status == "success") {
                    setAnalyticList(result.data);
                } else {
                    if (result.code == 401) {
                        Utils.logoutUser();
                    }
                    Swal.fire({
                        title: 'Error',
                        text: result.message,
                        icon: 'error',
                        confirmButtonText: 'OK',
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    })
                }
            })
    }

    const athletes = analyticList?.athletes ?? [];
    const maxValues = analyticList?.max_values ?? {};

    const metrics = [
        {
            key: "snatchCjRatio",
            label: "S/CJ Ratio",
            max: Number(maxValues?.snatchCjRatio) || 0
        },
        {
            key: "platformEfficiency",
            label: "Platform Eff.",
            max: Number(maxValues?.platformEfficiency) || 0
        },
        {
            key: "bodyweight",
            label: "Bodyweight",
            max: Number(maxValues?.bodyweight) || 0
        },
        {
            key: "best_total",
            label: "Best Total",
            max: Number(maxValues?.best_total) || 0
        },
        {
            key: "sinclair_score",
            label: "Sinclair Coefficients",
            max: Number(maxValues?.sinclair_score) || 0
        },
        {
            key: "robi_points",
            label: "Robi Points",
            max: Number(maxValues?.robi_points) || 0
        },
    ];

    const series = athletes.map((athlete: any, index: number) => ({
        // name: `Athlete ${athlete.athlete_id}`,
        name: index === 0 ? athlete1Name : athlete2Name,
        data: metrics.map(metric => {
            const value = Number(athlete[metric.key]) || 0;
            const max = Number(metric.max) || 0;
            return max > 0 ? Number(((value / max) * 100).toFixed(2)) : 0;
        })
    }));

    const radarOptions: ApexOptions = {

        chart: {
            type: "radar",
            toolbar: {
                show: false
            }
        },

        xaxis: {
            categories: metrics.map(x => x.label),
            labels: {
                style: {
                    fontSize: "13px",
                    fontWeight: 600,
                    colors: Array(metrics.length).fill("#374151")
                }
            }
        },

        yaxis: {
            min: 0,
            max: 100,
            tickAmount: 5,
            show: false
        },

        plotOptions: {
            radar: {
                size: 170,
                polygons: {
                    strokeColors: "#d1d5db",
                    connectorColors: "#d1d5db",
                    fill: {
                        colors: [
                            "#ffffff",
                            "#f8fafc"
                        ]
                    }
                }
            }
        },

        stroke: {
            width: 3,
            curve: "smooth"
        },

        fill: {
            opacity: 0.25
        },

        colors: [
            "#2563eb",
            "#f97316"
        ],

        markers: {
            size: 6,
            strokeWidth: 2,
            hover: {
                size: 8
            }
        },

        legend: {
            position: "bottom",
            horizontalAlign: "center",
            fontSize: "14px"
        },

        dataLabels: {
            enabled: false
        },

        tooltip: {
            y: {
                formatter: function (
                    value,
                    { seriesIndex, dataPointIndex }
                ) {

                    const athlete = athletes[seriesIndex];
                    const metric = metrics[dataPointIndex];

                    return `${athlete[metric.key]}`;
                }
            }
        }
    };

    return (
        <>

            {isLoading && (
                <div id="global-loader">
                    <div className="loader-img">
                        <ImageWithBasePath src="assets/img/loader.png" className="img-fluid" alt="Global" />
                    </div>
                </div>
            )}
            <div className="content container" style={{ maxWidth: "1500px" }}>
                <div className="row">
                    <div className="sortby-section">
                        <div className="sorting-info">
                            {/* <div className="row align-items-center">
                                <div className="col d-flex justify-content-between">
                                    <h4>Analytics</h4>
                                </div>
                            </div> */}
                            <div className="row">
                                <div className="col-lg-2">
                                    <div className="input-space">
                                        <label className="form-label">Athlete 1</label>
                                        <Select
                                            value={athleteList.find(athlt => athlt.extComAthleteId === athlete1Id) ? {
                                                value: athlete1Id,
                                                label: athleteList.find(athlt => athlt.extComAthleteId === athlete1Id).extComAthleteName
                                            } : null}
                                            onChange={async (e: any) => {
                                                if (e) {
                                                    setAthlete1Id(e.value);
                                                    setAthlete1Name(e.label);
                                                    const athltResponse = await func.getAthletesByWeight(e.value);
                                                    setAthleteListByWeight(Array.isArray(athltResponse) ? athltResponse : []);

                                                }
                                            }}
                                            // styles={customSelect}
                                            required
                                            options={athleteList.map(athlt => ({
                                                value: athlt.extComAthleteId,
                                                label: athlt.extComAthleteName
                                            }))}>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="input-space">
                                        <label className="form-label">Athlete 2</label>
                                        <Select
                                            value={athleteListByWeight.find(athlt => athlt.extComAthleteId === athlete2Id) ? {
                                                value: athlete2Id,
                                                label: athleteListByWeight.find(athlt => athlt.extComAthleteId === athlete2Id).extComAthleteName
                                            } : null}
                                            onChange={(e: any) => {
                                                if (e) {
                                                    setAthlete2Id(e.value);
                                                    setAthlete2Name(e.label);
                                                }
                                            }}
                                            // styles={customSelect}
                                            required
                                            options={athleteListByWeight.map(athlt => ({
                                                value: athlt.extComAthleteId,
                                                label: athlt.extComAthleteName
                                            }))}>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="input-space">
                                        <label className="form-label">Cycle</label>
                                        <Select
                                            value={sinclairList.find(snclr => snclr.extComOlympicCycle === cycleId) ? {
                                                value: cycleId,
                                                label: sinclairList.find(snclr => snclr.extComOlympicCycle === cycleId).extComOlympicCycle + "-" +
                                                    sinclairList.find(snclr => snclr.extComOlympicCycle === cycleId).extComGender
                                            } : null}
                                            onChange={(e: any) => {
                                                if (e) {
                                                    setCycleId(e.value);
                                                }
                                            }}
                                            // styles={customSelect}
                                            required
                                            options={sinclairList.map(snclr => ({
                                                value: snclr.extComOlympicCycle,
                                                label: snclr.extComOlympicCycle + "-" + snclr.extComGender
                                            }))}>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="input-space">
                                        <label className="form-label">Gender</label>
                                        <Select
                                            value={genderList.find(gender => gender.extComGenderName === genderId) ? {
                                                value: genderId,
                                                label: genderList.find(gender => gender.extComGenderName === genderId)?.extComGenderName ?? genderId
                                            } : null}
                                            onChange={(e: any) => {
                                                if (e) {
                                                    setGenderId(e.value);
                                                }
                                            }}
                                            // styles={customSelect}
                                            required
                                            options={genderList.map(gender => ({
                                                value: gender.extComGenderName,
                                                label: gender.extComGenderName
                                            }))}>
                                        </Select>
                                    </div>
                                </div>
                                <div className="col-lg-2" style={{ marginTop: "25px" }}>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={getAnalytics}
                                        className="w-100"
                                    >
                                        Submit
                                    </Button>
                                </div>

                                {athletes.length > 0 && (
                                    <>
                                        <div className="col-12 mt-4">
                                            <div className="card p-3">
                                                <h5 className="mb-3 text-center">Macro Level Comparision View</h5>
                                                <ReactApexChart
                                                    options={radarOptions}
                                                    series={series}
                                                    type="radar"
                                                    height={500}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 mt-4">
                                            <div className="card p-3">
                                                <h5 className="mb-3">Micro Level Attempt Matrix</h5>
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Metric</th>
                                                                {athletes.map((athlete: any, index) => (
                                                                    <th key={athlete.athlete_id}>
                                                                        {/* Athlete {athlete.athlete_id} */}
                                                                        {index === 0 ? athlete1Name : athlete2Name}

                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {metrics.map(metric => (
                                                                <tr key={metric.key}>
                                                                    <td>{metric.label}</td>
                                                                    {athletes.map((athlete: any) => (
                                                                        <td key={`${athlete.athlete_id}-${metric.key}`}>
                                                                            {athlete[metric.key] ?? "N/A"}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ExternalCompAnalytics;